/**
 * Security Configuration
 * Centralized security settings for the application
 */

// Allowed origins for CORS (production domains only)
export const ALLOWED_ORIGINS = [
  'https://vividad.site',
  'https://www.vividad.site',
  // Add your custom domain here when deployed
];

// Security headers for production
export const SECURITY_HEADERS = {
  // Prevent clickjacking attacks
  'X-Frame-Options': 'DENY',
  
  // Prevent MIME type sniffing
  'X-Content-Type-Options': 'nosniff',
  
  // Enable XSS protection
  'X-XSS-Protection': '1; mode=block',
  
  // Referrer policy
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  
  // Permissions policy (restrict dangerous APIs)
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=(), payment=()',
  
  // Content Security Policy
  'Content-Security-Policy': [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://accounts.google.com https://apis.google.com",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    "font-src 'self' https://fonts.gstatic.com",
    "img-src 'self' data: https: blob:",
    "connect-src 'self' https://*.supabase.co https://www.googletagmanager.com https://accounts.google.com",
    "frame-src 'self' https://accounts.google.com",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self'",
    "frame-ancestors 'none'",
    "upgrade-insecure-requests"
  ].join('; ')
};

// Rate limiting configuration
export const RATE_LIMITS = {
  // Maximum failed login attempts before temporary lockout
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION_MS: 15 * 60 * 1000, // 15 minutes
  
  // API request limits
  API_REQUESTS_PER_MINUTE: 60,
  API_REQUESTS_PER_HOUR: 1000,
};

// Session configuration
export const SESSION_CONFIG = {
  // JWT token expiration
  ACCESS_TOKEN_EXPIRY: 3600, // 1 hour
  REFRESH_TOKEN_EXPIRY: 604800, // 7 days
  
  // Session timeout (idle time)
  IDLE_TIMEOUT_MS: 30 * 60 * 1000, // 30 minutes
};

// Password requirements
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 100,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true,
  REQUIRE_SPECIAL_CHAR: false,
};

// Fraud detection thresholds
export const FRAUD_THRESHOLDS = {
  MAX_ACCOUNTS_PER_IP: 3,
  MAX_ACCOUNTS_PER_DEVICE: 3,
  SUSPICIOUS_ACTIVITY_SCORE: 50,
  AUTO_FLAG_SCORE: 75,
};

/**
 * Validates if origin is allowed
 */
export const isOriginAllowed = (origin: string | null): boolean => {
  if (!origin) return false;
  
  // In development, allow localhost
  if (import.meta.env.DEV && origin.includes('localhost')) {
    return true;
  }
  
  // In production, only allow configured origins
  return ALLOWED_ORIGINS.some(allowed => origin.startsWith(allowed));
};

/**
 * Gets CORS headers for the current environment
 */
export const getCorsHeaders = (origin: string | null = null) => {
  // In development, allow all origins
  if (import.meta.env.DEV) {
    return {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type, x-api-token, x-api-key',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    };
  }
  
  // In production, restrict to allowed origins
  const allowedOrigin = isOriginAllowed(origin) ? origin : ALLOWED_ORIGINS[0];
  
  return {
    'Access-Control-Allow-Origin': allowedOrigin,
    'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type, x-api-token, x-api-key',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Credentials': 'true',
  };
};
